import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import { Button, ShimmerDiv } from 'fluxo-ui';
import { BarChartIcon, CalendarIcon, PlayIcon, PlusIcon, QuestionCircle, UserGroupIcon } from 'fluxo-ui/icons';

import { Card, EmptyState, PageHeader, StatCard } from '../common';
import analyticsService from '../../services/analytics.service';
import examService from '../../services/exam.service';
import { useAuthStore } from '../../store/authStore';

import type { StatTone } from '../common/StatCard';

interface InstructorStats {
    myExams: number;
    myQuestions: number;
    totalAttempts: number;
    avgScore: number;
    activeExams: number;
    pendingGrading: number;
}

interface ActiveExamItem {
    id: string;
    title: string;
    questions: number;
    endDate: string;
}

const emptyStats: InstructorStats = {
    myExams: 0,
    myQuestions: 0,
    totalAttempts: 0,
    avgScore: 0,
    activeExams: 0,
    pendingGrading: 0,
};

const quickActionChipClasses: Record<StatTone, string> = {
    blue: 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    violet: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    sky: 'bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400',
    gray: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const InstructorDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [stats, setStats] = useState<InstructorStats | null>(null);
    const [myActiveExams, setMyActiveExams] = useState<ActiveExamItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const [data, examsResponse] = await Promise.all([
                    analyticsService.getInstructorStats().catch(() => null),
                    examService.listExams({ status: 'active', limit: 6 }).catch(() => ({ exams: [] }) as any),
                ]);
                setStats(data || emptyStats);
                setMyActiveExams(
                    (examsResponse?.exams || []).map((e: any) => ({
                        id: e._id,
                        title: e.title,
                        questions: e.questions?.length || 0,
                        endDate: e.endDateTime ? new Date(e.endDateTime).toLocaleDateString() : 'No deadline',
                    })),
                );
            } catch (error) {
                console.error('Failed to fetch instructor dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const quickActions: { label: string; description: string; icon: React.ComponentType<{ className?: string }>; action: () => void; tone: StatTone }[] = [
        { label: 'Create New Exam', description: 'Build a new assessment', icon: PlusIcon, action: () => navigate('/exams/create'), tone: 'blue' },
        { label: 'Add Questions', description: 'Build question bank', icon: QuestionCircle, action: () => navigate('/questions/create'), tone: 'emerald' },
        { label: 'Start Live Quiz', description: 'Kahoot-style quiz', icon: PlayIcon, action: () => navigate('/live-quiz'), tone: 'violet' },
        { label: 'View Analytics', description: 'Performance insights', icon: BarChartIcon, action: () => navigate('/analytics'), tone: 'amber' },
    ];

    if (loading) {
        return (
            <div className="space-y-8">
                <ShimmerDiv level={2} className="h-20 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <ShimmerDiv key={i} level={3} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    const userName = user?.profile?.name || user?.name || user?.email || '';

    return (
        <div className="space-y-6">
            <PageHeader
                title={userName ? `Welcome back, ${userName}!` : 'Welcome back!'}
                description="Here's an overview of your assessments"
                className="mb-0"
            />

            {stats && stats.pendingGrading > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {stats.pendingGrading} submissions pending grading
                    </span>
                    <Button label="Grade Now" variant="warning" size="sm" onClick={() => navigate('/results')} />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                <StatCard
                    label="My Exams"
                    value={stats?.myExams ?? 0}
                    icon={CalendarIcon}
                    tone="blue"
                    hint={`${stats?.activeExams ?? 0} active`}
                    onClick={() => navigate('/exams')}
                />
                <StatCard
                    label="My Questions"
                    value={stats?.myQuestions ?? 0}
                    icon={QuestionCircle}
                    tone="emerald"
                    onClick={() => navigate('/questions')}
                />
                <StatCard
                    label="Total Attempts"
                    value={stats?.totalAttempts ?? 0}
                    icon={UserGroupIcon}
                    tone="violet"
                    onClick={() => navigate('/results')}
                />
                <StatCard
                    label="Avg. Score"
                    value={`${stats?.avgScore ?? 0}%`}
                    icon={BarChartIcon}
                    tone="amber"
                    hint={`${stats?.pendingGrading ?? 0} pending grading`}
                    onClick={() => navigate('/analytics')}
                />
            </div>

            <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {quickActions.map((action) => {
                        const Icon = action.icon;
                        return (
                            <button
                                key={action.label}
                                onClick={action.action}
                                className="group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-teal-300 dark:hover:border-teal-700 hover:shadow-sm transition-all p-5 text-left"
                            >
                                <div
                                    className={classNames(
                                        'w-10 h-10 rounded-lg flex items-center justify-center mb-3',
                                        quickActionChipClasses[action.tone],
                                    )}
                                >
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                                    {action.label}
                                </div>
                                <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{action.description}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">My Active Exams</h2>
                    <Button label="View All" variant="default" size="sm" layout="outlined" onClick={() => navigate('/exams')} />
                </div>
                {myActiveExams.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {myActiveExams.map((exam) => (
                            <Card key={exam.id} interactive onClick={() => navigate(`/exams/${exam.id}/edit`)}>
                                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">{exam.title}</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                                        <span>Questions</span>
                                        <span className="font-medium text-slate-900 dark:text-white tabular-nums">{exam.questions}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                                        <span>Ends</span>
                                        <span className="font-medium text-amber-600 dark:text-amber-400">{exam.endDate}</span>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card>
                        <EmptyState
                            icon={CalendarIcon}
                            title="No active exams"
                            description="Create an exam to start assessing your students"
                            action={<Button label="Create New Exam" variant="primary" onClick={() => navigate('/exams/create')} />}
                        />
                    </Card>
                )}
            </div>
        </div>
    );
};

export default InstructorDashboard;
